// Imports
import { faArrowsLeftRight, faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { lazy, Suspense, useState } from 'react';
import styled from 'styled-components/macro';
import Select from '../../../../shareables/foundation/front-end/components/forms/fields/select';
import availableSubscriptionSizes from '../../../../shareables/utils/billing/available-subscription-sizes';
import planMapping from '../../../../shareables/utils/billing/plan-mapping';
import subscriptionAmountMapping from '../../../../shareables/utils/billing/subscription-amount-mapping';
import extendedPricingDetails from '../../../../shareables/utils/billing/extended-pricing-details';


// Lazily-loaded components
const ReactSlider = lazy(() => import('react-slider'));


// Styled components
const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 2.5rem 3.75rem;
	gap: 1rem;
	margin-top: 3.75rem;
	
	background: hsl(206, 20%, 96%);
	border-radius: 1.25rem;
	
	color: hsl(206, 25%, 20%);
	
	.slider {
		width: 15.625rem;
		height: 1.75rem;
		
		@media (max-width: 37.5rem) {
			width: 100%;
		}
		
		> .thumb {
			:focus-visible {
				outline: 0;
				
				svg:first-child {
					outline: -webkit-focus-ring-color auto 0.0625rem;
				}
			}
			
			@media (max-width: 37.5rem) {
				top: -0.875rem;
			}
		}
	}
	
	@media (max-width: 48rem) {
		width: 100%;
	}
`;

const Heading = styled.h2`
	font-weight: 600;
	font-size: 1.5rem;
	line-height: 1.2;
	text-align: center;
`;

const SliderRow = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
	height: 2.1875rem;
	
	select {
		background-color: hsl(206, 20%, 86%);
		height: 1.875rem;
		border: none;
	}
	
	select + span {
		top: -0.2188rem;
	}
	
	@media (max-width: 37.5rem) {
		flex-direction: column;
		margin-top: 1rem;
		margin-bottom: 2rem;
		gap: 2rem;
	}
`;

const IconLayers = styled.span`
	font-size: 1.75rem;
	cursor: pointer;
	left: -0.21875rem;
	position: absolute;
	
	--fa-border-color: hsl(206, 20%, 96%);
	--fa-border-radius: 50%;
	--fa-border-width: 0.125rem;
	--fa-border-padding: 0;
`;

const ThumbContainer = styled.div`
	position: relative;
	width: 1.75rem;
	height: 1.75rem;
`;

const renderThumb: React.ComponentProps<typeof ReactSlider>['renderThumb'] = (props) => (
	<div {...props}>
		<ThumbContainer>
			<IconLayers className='fa-layers fa-fw'>
				<FontAwesomeIcon icon={faCircle} color='hsl(206, 25%, 20%)' border />
				<FontAwesomeIcon icon={faArrowsLeftRight} color='hsl(0, 0%, 100%)' transform='shrink-5.71' />
			</IconLayers>
		</ThumbContainer>
	</div>
);

const SliderTrack = styled.div<{ index: number }>`
	height: 0.1875rem;
	background-color: hsl(206, 25%, 20%);
	
	position: absolute;
	top: calc(50% - 0.0625rem);
`;

const renderTrack: React.ComponentProps<typeof ReactSlider>['renderTrack'] = (props, state) => (
	<SliderTrack
		{...(props as unknown as Omit<
			React.DetailedHTMLProps<React.HTMLProps<HTMLDivElement>, HTMLDivElement>,
			'ref' | 'as'
		>)}
		index={state.index}
	/>
);

const HouseholdRange = styled.span`
	min-width: 14.6875rem;
	font-size: 1.25rem;
	
	@media (max-width: 37.5rem) {
		text-align: center;
		min-width: 14.0625rem;
	}
`;


// Define the accepted props
interface Props {
	sizeIndex: number;
	setSizeIndex: React.Dispatch<React.SetStateAction<number>>;
	storybookSliderMaxReached?: true;
}


// Function component
const HouseholdSlider: React.FC<Props> = ({ sizeIndex, setSizeIndex, storybookSliderMaxReached }) => {
	// Use state
	const [sliderMaxReached, setSliderMaxReached] = useState(storybookSliderMaxReached ?? false);
	
	
	// Initialize
	const allSizes = availableSubscriptionSizes(planMapping.engage);
	
	const baseSizeSet = new Set(Object.keys(subscriptionAmountMapping[planMapping.engage]).map((size) => Number(size)));
	
	const [basePlanSizes, extendedPlanSizes] = allSizes.reduce<[number[], number[]]>(
		([base, extended], size) => {
			if (baseSizeSet.has(size)) {
				base.push(size);
			} else {
				extended.push(size);
			}
			
			return [base, extended];
		},
		[[], []]
	);
	
	const extendedPricingStep = extendedPricingDetails[planMapping.engage].step;
	
	
	// Return JSX
	return (
		<Container>
			<Heading>How many households are in your community?</Heading>
			
			<SliderRow>
				<Suspense fallback={<div className='slider' />}>
					<ReactSlider
						value={sliderMaxReached ? basePlanSizes.length : sizeIndex}
						min={0}
						max={basePlanSizes.length}
						onChange={(newValue) => {
							if (!(typeof newValue === 'number')) {
								throw new Error('This should not be possible');
							}
							
							if (newValue === basePlanSizes.length) {
								setSliderMaxReached(true);
								setSizeIndex(newValue - 1);
							} else {
								setSliderMaxReached(false);
								setSizeIndex(newValue);
							}
						}}
						renderThumb={renderThumb}
						renderTrack={renderTrack}
						ariaValuetext={`${availableSubscriptionSizes(planMapping.engage)[sizeIndex].toLocaleString()} households`}
						ariaLabel='Community size'
					/>
				</Suspense>
				
				{sliderMaxReached ? (
					<Select
						name='selectSize'
						placeholder='Select size…'
						onChange={(newValue) => {
							setSizeIndex(Number(newValue) + basePlanSizes.length);
						}}
					>
						{extendedPlanSizes.map((size, index) => (
							<option key={size} value={index}>
								{(size - extendedPricingStep + 1).toLocaleString()}-{size.toLocaleString()} households
							</option>
						))}
					</Select>
				) : (
					<HouseholdRange>
						{(basePlanSizes[sizeIndex - 1] ? basePlanSizes[sizeIndex - 1] + 1 : 0).toLocaleString()}-
						{basePlanSizes[sizeIndex].toLocaleString()} households
					</HouseholdRange>
				)}
			</SliderRow>
		</Container>
	);
};

export default HouseholdSlider;
